import { Helmet } from 'react-helmet';
import React, { useState, useEffect } from 'react';
import { Alert, Modal, Button, Spinner } from 'react-bootstrap';
import FileList from '../../components/admin/FileList';
import FileEdit from '../../components/admin/FileEdit';
import { getAdminFiles, updateFileAdmin, deleteFile } from './../../api';

function AdminResource() {
  // eslint-disable-next-line
  const title = 'List of All Resources';
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [alert, setAlert] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filesPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    try {
      setIsLoading(true);  // Start loading
      const response = await getAdminFiles();
      setFiles(response.data);
    } catch (error) {
      setAlert({ variant: 'danger', message: 'Failed to fetch files' });
    } finally {
      setIsLoading(false);  // End loading regardless of success/failure
    }
  };

  const handleEditFile = (file) => {
    setSelectedFile(file);
    setShowModal(true);
  };


  const handleDeleteShow = (file) => {
    setSelectedFile(file);
    setShowDeleteModal(true);
  };

  const handleDeleteClose = () => {
    setShowDeleteModal(false);
    setSelectedFile(null);
  };

  const handleDeleteFile = async () => {
    try {
      await deleteFile(selectedFile.id);
      fetchFiles();
      handleDeleteClose();
    } catch (error) {
      console.error('Error deleting file:', error);
    }
  };


  const handleUpdateFile = async (updatedFile) => {
    try {
      await updateFileAdmin(updatedFile.id, updatedFile);
      fetchFiles();
      setShowModal(false);
      setSelectedFile(null);
      setAlert({ variant: 'success', message: 'File updated successfully' });
    } catch (error) {
      setAlert({ variant: 'danger', message: 'Failed to update file' });
    }
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedFiles = React.useMemo(() => {
    let sortableFiles = [...files];
    if (sortConfig.key !== null) {
      sortableFiles.sort((a, b) => {
        if (sortConfig.key === 'is_active') {
          // Handle cases where is_active might not exist
          const aValue = a.is_active === undefined ? false : a.is_active;
          const bValue = b.is_active === undefined ? false : b.is_active;
          return sortConfig.direction === 'ascending'
            ? (aValue === bValue ? 0 : aValue ? -1 : 1)
            : (aValue === bValue ? 0 : aValue ? 1 : -1);
        } else {
          if (a[sortConfig.key] < b[sortConfig.key]) {
            return sortConfig.direction === 'ascending' ? -1 : 1;
          }
          if (a[sortConfig.key] > b[sortConfig.key]) {
            return sortConfig.direction === 'ascending' ? 1 : -1;
          }
        }
        return 0;
      });
    }
    return sortableFiles;
  }, [files, sortConfig]);

  const filteredFiles = sortedFiles.filter(file =>
    file.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    file.user_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (file.is_active !== undefined && file.is_active.toString().includes(searchTerm.toLowerCase()))
  );

  const indexOfLastFile = currentPage * filesPerPage;
  const indexOfFirstFile = indexOfLastFile - filesPerPage;
  const currentFiles = filteredFiles.slice(indexOfFirstFile, indexOfLastFile);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <div className="container-fluid mb-5 px-3">
        <div className="row g-5 pt-3">
          <div className="col-12">
            <div className="card">
              <div className="card-header pt-3 bg-white">

                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h4 className="text-secondary">Resources Directory</h4>
                  </div>

                </div>
              </div>
              <div className="table-responsive position-relative">
                {isLoading ? (
                  <div className="text-center mt-5">
                    <Spinner
                      animation="border"
                      variant="primary"
                      role="status"
                      style={{ width: "3rem", height: "3rem" }}
                    >
                      <span className="visually-hidden">Loading...</span>
                    </Spinner>
                    <p className="mt-2">Loading user data...</p>
                  </div>
                ) : (
                  <FileList
                    files={currentFiles}
                    onEditFile={handleEditFile}
                    onDeleteFile={handleDeleteShow}
                    onSearch={handleSearch}
                    onSort={handleSort}
                    sortConfig={sortConfig}
                    currentPage={currentPage}
                    filesPerPage={filesPerPage}
                    totalFiles={filteredFiles.length}
                    paginate={paginate}

                  />
                )}
              </div>
            </div>
          </div>
          <div>
            {alert && (
              <Alert variant={alert.variant} onClose={() => setAlert(null)} dismissible>
                {alert.message}
              </Alert>
            )}
          </div>
        </div >
      </div >

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit File</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedFile && (
            <FileEdit
              file={selectedFile}
              onUpdateFile={handleUpdateFile}
              onCancel={() => setShowModal(false)}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleDeleteClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the file "{selectedFile?.name}"?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleDeleteClose}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteFile}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

    </>
  );
}

export default AdminResource;